import React, { useMemo } from "react";

import {
  BasketballActionType,
  HockeyActionType,
  MidPoint,
  PointType,
  SoccerActionLineType,
  Action as TAction,
} from "../../../types";
import { isClassicEndPoint } from "../../../utils";
import {
  BlockLine,
  DottedLine,
  Handoff,
  Hashpass,
  Shoot,
  SkateBackwards,
  SolidLine,
  SquiggleLine,
} from "../../action-lines";
import { StencilWrapper } from "../../stencil-wrapper";

/** ================================
 * Get Offset Control Points
 ================================ */
/** calculates control points of action line relative to start position, where start is 0, 0 and everything else
 * relative to that */
type GetOffsetControlPointsProps = TAction;
const getOffsetControlPoints = (props: GetOffsetControlPointsProps): MidPoint[] => {
  const { pos, midPoints, endPos } = props;
  const startX = pos.x;
  const startY = pos.y;
  const endX = endPos.x;
  const endY = endPos.y;

  // for each point, subject the start position to get the relative position
  if (midPoints.length === 0) {
    return [
      { x: 0, y: 0, pointType: PointType.START },
      { x: endX - startX, y: endY - startY, pointType: PointType.END },
    ];
  } else {
    return [
      { x: 0, y: 0, pointType: PointType.START },
      ...midPoints.map((point) => ({ ...point, x: point.x - startX, y: point.y - startY })),
      { x: endX - startX, y: endY - startY, pointType: PointType.END },
    ];
  }
};

/** ================================
 * Action
 ================================ */
export type ActionProps = {
  action: TAction;
  animateable?: boolean;
};

export const Action = ({ action, animateable = true }: ActionProps) => {
  const { layerType } = action;

  const actionHasClassicEndPoint = isClassicEndPoint(action);

  const offsetControlPoints = useMemo(
    () =>
      getOffsetControlPoints({
        ...action,
        ...(actionHasClassicEndPoint && { endPos: action?.classicEndPoint }),
      }),
    [action, actionHasClassicEndPoint]
  );

  return (
    <StencilWrapper stencil={action} controlPoints={offsetControlPoints}>
      {({ controlPoints }): JSX.Element => {
        switch (layerType) {
          case BasketballActionType.CUT:
          case HockeyActionType.SKATE:
          case SoccerActionLineType.SC_PASS:
            return (
              <SolidLine
                action={action}
                controlPoints={controlPoints}
                classicMarkerHead={actionHasClassicEndPoint}
                animateable={animateable}
              />
            );

          case HockeyActionType.SKATE_BACKWARDS:
            return (
              <SkateBackwards action={action} controlPoints={controlPoints} size={0.25} animateable={animateable} />
            );

          case BasketballActionType.PASS:
          case HockeyActionType.PASS:
          case SoccerActionLineType.SC_RUN:
          case SoccerActionLineType.SC_RUN_AND_STOP:
            return (
              <DottedLine
                action={action}
                controlPoints={controlPoints}
                classicMarkerHead={actionHasClassicEndPoint}
                animateable={animateable}
              />
            );

          case HockeyActionType.SHOOT:
            return <Shoot action={action} controlPoints={controlPoints} animateable={animateable} />;
          case SoccerActionLineType.SC_SHOT:
            return (
              <Shoot action={action} controlPoints={controlPoints} animateable={animateable} coverColor="#5DB04B" />
            );

          case BasketballActionType.SCREEN:
            return <BlockLine action={action} controlPoints={controlPoints} animateable={animateable} />;

          case BasketballActionType.DRIBBLE:
          case HockeyActionType.SKATE_WITH_PUCK:
          case HockeyActionType.SKATE_BACKWARDS_WITH_PUCK:
          case SoccerActionLineType.SC_DRIBBLE:
          case SoccerActionLineType.SC_DRIBBLE_AND_STOP:
            return (
              <SquiggleLine
                action={action}
                controlPoints={controlPoints}
                size={0.15}
                classicMarkerHead={actionHasClassicEndPoint}
                animateable={animateable}
              />
            );

          case BasketballActionType.HASHPASS:
            return (
              <Hashpass
                action={action}
                controlPoints={controlPoints}
                numberOfHashes={action.numberOfHashes}
                classicMarkerHead={actionHasClassicEndPoint}
                animateable={animateable}
              />
            );

          case BasketballActionType.HANDOFF:
          case HockeyActionType.HANDOFF:
          case SoccerActionLineType.SC_HANDOFF:
            return <Handoff />;

          case BasketballActionType.CUT_RECEIVING_HANDOFF:
          case BasketballActionType.SCREEN_RECEIVING_HANDOFF:
          case HockeyActionType.SKATE_BACKWARDS_RECEIVING_HANDOFF:
          case HockeyActionType.SKATE_LATERAL:
          case HockeyActionType.SKATE_LATERAL_RECEIVING_HANDOFF:
          case HockeyActionType.SKATE_RECEIVING_HANDOFF:
          case SoccerActionLineType.SC_RUN_RECEIVING_HANDOFF:
          case SoccerActionLineType.SC_RUN_AND_STOP_RECEIVING_HANDOFF:
            return (
              <SolidLine
                action={action}
                controlPoints={controlPoints}
                classicMarkerHead={actionHasClassicEndPoint}
                animateable={animateable}
              />
            );
        }
      }}
    </StencilWrapper>
  );
};
